import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api';
import { queryKeys } from '../lib/queryClient';

// Health check hook
export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health(),
    queryFn: () => apiClient.getHealth(),
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Users hooks
export function useUsers(filters: Record<string, any> = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.users.list(filters),
    queryFn: () => apiClient.getUsers(filters),
    enabled: options.enabled !== false,
  });
}

export function useUser(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.users.detail(id),
    queryFn: () => apiClient.getUser(id),
    enabled: enabled && !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.createUser(data),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.updateUser(id, data),
    onSuccess: (_, { id }) => {
      // Invalidate specific user and users list
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteUser(id),
    onSuccess: () => {
      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

// Employees hooks
export function useEmployees(filters: Record<string, any> = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.employees.list(filters),
    queryFn: () => apiClient.getEmployees(filters),
    enabled: options.enabled !== false,
  });
}

export function useEmployee(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.employees.detail(id),
    queryFn: () => apiClient.getEmployee(id),
    enabled: enabled && !!id,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.lists() });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.updateEmployee(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.lists() });
    },
    // Optimistic updates for better UX
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: queryKeys.employees.detail(id) });

      // Snapshot the previous value
      const previousEmployee = queryClient.getQueryData(queryKeys.employees.detail(id));

      // Optimistically update to the new value
      if (previousEmployee) {
        queryClient.setQueryData(queryKeys.employees.detail(id), {
          ...previousEmployee,
          data: { ...(previousEmployee as any).data, ...data },
        });
      }

      // Return a context object with the snapshotted value
      return { previousEmployee };
    },
    onError: (err, { id }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousEmployee) {
        queryClient.setQueryData(queryKeys.employees.detail(id), context.previousEmployee);
      }
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.employees.lists() });
    },
  });
}

// Departments hooks
export function useDepartments(filters: Record<string, any> = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.departments.list(filters),
    queryFn: () => apiClient.getDepartments(filters),
    enabled: options.enabled !== false,
  });
}

export function useDepartment(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.departments.detail(id),
    queryFn: () => apiClient.getDepartment(id),
    enabled: enabled && !!id,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.createDepartment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.lists() });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.updateDepartment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.lists() });
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteDepartment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.departments.lists() });
    },
  });
}

// Locations hooks
export function useLocations(filters: Record<string, any> = {}, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.locations.list(filters),
    queryFn: () => apiClient.getLocations(filters),
    enabled: options.enabled !== false,
  });
}

export function useLocation(id: string, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.locations.detail(id),
    queryFn: () => apiClient.getLocation(id),
    enabled: enabled && !!id,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.createLocation(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      apiClient.updateLocation(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.deleteLocation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.locations.lists() });
    },
  });
}

// Time Records hooks
export function useTimeRecords(filters: Record<string, any> = {}) {
  return useQuery({
    queryKey: queryKeys.timeRecords.list(filters),
    queryFn: () => apiClient.getTimeRecords(filters),
  });
}

// Attendance Events hooks
export function useAttendanceEvents(
  filters: Record<string, any> = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: queryKeys.attendance.event(filters),
    queryFn: () => apiClient.getAttendanceEvents(filters),
    enabled: options?.enabled,
  });
}

export function useCreateAttendanceEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => apiClient.createAttendanceEvent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.attendance.events() });
    },
  });
}